import { useMemo } from 'react';

import {
	ActivityLogIcon,
	BackorderedProductsIcon,
	BudgetIcon,
	DistroOrdersIcon,
	Products,
	Success,
} from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import type { DashboardCard } from '@apple/ui/shell/components/Dashboard';

import { useAuthContext } from '@apple/features/auth-retail/hooks/useAuthContext';

export function useAppleRetailAdminDashboard() {
	const { t } = useTranslation('admin-dashboard');
	const { filterByPermissions } = useAuthContext();

	const cards = useMemo(() => {
		return filterByPermissions<DashboardCard>([
			{
				title: t('cards.distroOrders'),
				icon: DistroOrdersIcon,
				to: '/distro/history',
				datatestId: 'distro-history',
				color: 'teal.6',
				requirePermission: 'Manzanita.Security.Features.DistroOrderHistory',
			},
			{
				title: t('cards.orderApproval'),
				icon: Success,
				to: '/approvals/orders',
				color: 'violet.4',
				requirePermission: 'Manzanita.Security.Features.OrderManagement.Read',
			},
			{
				title: t('cards.products'),
				icon: Products,
				to: '/manage/products',
				datatestId: 'admin-product',
				color: 'blue.4',
				requirePermission: 'Manzanita.Security.Features.ProductManagement.Read',
			},
			{
				title: t('cards.budgetReport'),
				icon: BudgetIcon,
				to: '/reports/budgets',
				datatestId: 'budget-report',
				color: 'green.4',
				requireAuth: true,
			},
			{
				title: t('cards.backorderedProducts'),
				icon: BackorderedProductsIcon,
				to: '/reports/backorders',
				color: 'cyan.4',
				requireAuth: true,
			},
			{
				title: t('cards.activityLog'),
				icon: ActivityLogIcon,
				to: '/reports/activity-log',
				datatestId: 'activity-log',
				color: 'indigo.4',
				requirePermission: 'AppleRetail.Security.Features.ActivityLog',
			},
		]);
	}, [filterByPermissions, t]);

	return { cards };
}
